// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// Terminal report rendering (ADR-006 D4). Halt-and-report is a valid result,
// not a crash (README). Copy never discloses whether a device exists
// (ADR-003 D3 / ADR-006 D3): not-found and out-of-scope share one message.

import { Ban, FileWarning, SearchX, TimerOff } from "lucide-react";
import type { ReactNode } from "react";

import type { HaltReason, QueryOutcome } from "@/contracts/outcome";
import { Alert, AlertDescription, AlertTitle } from "@/primitives/alert";

type Tone = "info" | "warning" | "error";

interface ReportCopy {
  readonly tone: Tone;
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: string;
}

function notFoundCopy(): ReportCopy {
  return {
    tone: "info",
    icon: <SearchX className="size-5 text-nc-info" aria-hidden />,
    // Deliberately non-disclosing: covers both "no such device" and
    // "outside your accessible view" without revealing which.
    title: "No device to show",
    description:
      "No device matches that identifier in the view available to you. Check the serial number or software ID and try again.",
  };
}

function haltCopy(reason: HaltReason, detail?: string): ReportCopy {
  switch (reason) {
    case "unauthorized":
      return {
        tone: "warning",
        icon: <Ban className="size-5 text-nc-warning" aria-hidden />,
        title: "Not authorized",
        description:
          "Your authorization profile does not permit this query. Contact your administrator if you believe this is a mistake.",
      };
    case "meta_resolution_failed":
      return {
        tone: "error",
        icon: <FileWarning className="size-5 text-nc-error" aria-hidden />,
        title: "Could not read the operation details",
        description:
          "The production details for this device's update operation could not be read, so the status is not shown. Try again shortly; if it persists, report it.",
      };
    case "audit_write_failed":
      return {
        tone: "error",
        icon: <FileWarning className="size-5 text-nc-error" aria-hidden />,
        title: "Query recorded incompletely",
        description:
          "The status was read but the query record could not be written. Report this so the audit trail can be reconciled.",
      };
    case "ambiguous":
      return {
        tone: "info",
        icon: <SearchX className="size-5 text-nc-info" aria-hidden />,
        title: "More than one match",
        description:
          "That identifier matches more than one device. Enter a more specific serial number or software ID.",
      };
    case "duplicate_device":
      return {
        tone: "warning",
        icon: <Ban className="size-5 text-nc-warning" aria-hidden />,
        title: "Registered as both demo and production",
        description:
          "This identifier is registered in both the demo and production device registries. Report this to have the duplicate registration resolved.",
      };
    case "bridge_timeout":
      return {
        tone: "warning",
        icon: <TimerOff className="size-5 text-nc-warning" aria-hidden />,
        title: "Timed out",
        description:
          "The request did not complete in time. Try again in a moment.",
      };
    case "bridge_error":
    default:
      return {
        tone: "error",
        icon: <FileWarning className="size-5 text-nc-error" aria-hidden />,
        title: "Something went wrong",
        description:
          detail && detail.length > 0
            ? `The query could not be completed: ${detail}`
            : "The query could not be completed. Try again shortly.",
      };
  }
}

export function HaltReport({ outcome }: { outcome: QueryOutcome }) {
  let copy: ReportCopy;
  if (outcome.kind === "not_found_or_out_of_scope") {
    copy = notFoundCopy();
  } else if (outcome.kind === "halted") {
    copy = haltCopy(outcome.reason, outcome.detail);
  } else {
    return null;
  }

  return (
    <Alert tone={copy.tone} icon={copy.icon}>
      <AlertTitle>{copy.title}</AlertTitle>
      <AlertDescription>{copy.description}</AlertDescription>
    </Alert>
  );
}
